import classNames from 'classnames';
import React from 'react';

import Button from '../Button/index.js';
import Typography from '../Typography/index.js';

import {
  AlertOctagon,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
} from '../Icon/index.js';
import { parseError } from '../../utils/errors.js';

export type AlertVariant = 'info' | 'warning' | 'danger' | 'success';

export interface AlertCta {
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * The label for the CTA.
   */
  label: string;

  /**
   * The action to take when clicking on the CTA.
   */
  onClick: React.MouseEventHandler;
}

export interface AlertProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * Call to action object props.
   */
  cta?: AlertCta;

  /**
   * An error to display as the main text.
   * Passing an error changes the default variant to `danger`.
   */
  error?: Error | unknown;

  /**
   * Hides the alert icon, if true.
   * @default false
   */
  hideIcon?: boolean;

  /**
   * Plain alerts don't have an outline or background.
   * Should not be used with a title or CTA.
   * @default false
   */
  plain?: boolean;

  /**
   * The title text to display in the Alert.
   */
  title?: string;

  /**
   * The alert variant to use.
   * @default "info", or "danger" if an error is passed
   */
  variant?: AlertVariant;
}

/**
 * We use alerts within all of our apps, and have some slightly different methods of applying them to the product.
 * However, the styling of all alerts follow the same formats regardless of which app they are being used in.
 *
 * Informational alerts are used to display supplimentary information to help support a header with additional context,
 * or give additional information on the expected behavior of a feature.
 * Treat these as “low priority”, and use them when you don’t need to go into as much depth as a contextual helper.
 * These alerts always fill the full container space available.
 * The icon should be aligned with the top line of text, unless the text only spans 1 line.
 * When the text only spans 1 line, it should be aligned vertically with the icon.
 * Info alerts use the same icon for every instance.
 *
 * These medium priority alerts are used when there is a need to increase visual attention to the alert.
 * They will typically be associated with components that may have an impact on the user.
 * For example: Saving an update on an item that will impact other items down the chain.
 *
 * Danger alerts are the highest priority alerts, and are used sparingly.
 * These alerts are really meant to provide context for actions that will have a large impact on the user.
 * For example: Deleting an item that will not be reversible.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1812%3A904
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      children,
      className,
      cta,
      error,
      hideIcon = false,
      plain = false,
      title,
      variant,
    },
    ref,
  ) => {
    const hasError = !!error;
    const _variant: AlertVariant = variant || (hasError ? 'danger' : 'info');
    const getIcon = () => {
      switch (_variant) {
        case 'warning':
          return AlertTriangle;
        case 'danger':
          return AlertOctagon;
        case 'info':
          return AlertCircle;
        case 'success':
          return CheckCircle;
      }
    };

    const Icon = getIcon();

    return (
      <div
        className={classNames(
          'alert-ds',
          `alert-${_variant}`,
          { plain },
          className,
        )}
        role="alert"
        ref={ref}
      >
        {!hideIcon && <Icon size="large" className="icon" />}
        <div className="content-container">
          <div className="text-container">
            {title && (
              <Typography variant="title-medium" bold>
                {title}
              </Typography>
            )}
            {!!children && (
              <Typography variant="paragraph-medium">{children}</Typography>
            )}
            {hasError && (
              <Typography variant="paragraph-medium">
                {parseError(error)}
              </Typography>
            )}
          </div>
          {cta && (
            <Button
              aria-disabled={cta.disabled}
              disabled={Boolean(cta.disabled)}
              onClick={cta.onClick}
              variant="text"
            >
              {cta.label}
            </Button>
          )}
        </div>
      </div>
    );
  },
);

Alert.displayName = 'Alert';

export default Alert;
